/* [Custom section with details of what it does by Saman Akram] */
/* Created on 2026-03-05. */
/* Updated on 2026-08-28. Baby SVG decorations arranged as a staggered full-section pattern with per-tab customizer colors. */

if (!customElements.get('sa-fst26-tabs')) {
  class SaFst26Tabs extends HTMLElement {
    connectedCallback() {
      this.displayType = this.dataset.displayType || 'carousel';
      this.shell = this.closest('.sa-fst26')?.querySelector('.sa-fst26__shell');
      this.tabList = this.querySelector('.sa-fst26__tablist');
      this.buttons = Array.from(this.querySelectorAll('[data-sa-fst26-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-sa-fst26-panel]'));

      if (!this.buttons.length || !this.panels.length) return;

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onWindowLoad = this.resyncActivePanel.bind(this);

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.onTabClick);
        button.addEventListener('keydown', this.onTabKeydown);
      });
      this.installStabilityWatchers();

      const sectionLabel = (this.tabList?.getAttribute('aria-label') || '').toLowerCase();
      if (sectionLabel.includes('baby')) this.installBabyDecor();

      const initialActive = this.panels.find((panel) => panel.classList.contains('is-active'));
      if (initialActive) {
        this.syncBackground(initialActive);
        this.resyncPanel(initialActive, { resetPosition: true });
      }

      this.scheduleResync(900);

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest('[data-sa-fst26-panel]');
          if (!panel) return;
          this.activate(panel.dataset.saFst26Panel, true);
        });
      }
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => {
        button.removeEventListener('click', this.onTabClick);
        button.removeEventListener('keydown', this.onTabKeydown);
      });
      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('pageshow', this.onWindowLoad);
      if (this.resyncTimer) clearTimeout(this.resyncTimer);
    }

    installBabyDecor() {
      if (!this.shell) return;

      this.shell.querySelectorAll(
        '.sa-fst26__baby-icons, .sa-fst26__baby-decor, .sa-fst26__baby-pattern'
      ).forEach((node) => node.remove());
      this.querySelectorAll('.sa-fst26__baby-decor').forEach((node) => node.remove());

      let style = document.getElementById('sa-fst26-baby-icon-styles');
      if (!style) {
        style = document.createElement('style');
        style.id = 'sa-fst26-baby-icon-styles';
        document.head.appendChild(style);
      }

      style.textContent = `
        .sa-fst26 .sa-fst26__school-shapes {
          display: none !important;
        }

        .sa-fst26 .sa-fst26__shell {
          position: relative;
          isolation: isolate;
        }

        .sa-fst26 .sa-fst26__baby-pattern {
          position: absolute;
          inset: 0;
          z-index: 1;
          overflow: hidden;
          pointer-events: none;
        }

        .sa-fst26 .section-header,
        .sa-fst26 .sa-fst26__tabs {
          position: relative;
          z-index: 2;
        }

        .sa-fst26 .sa-fst26__baby-pattern-icon {
          position: absolute;
          left: var(--x);
          top: var(--y);
          width: var(--size);
          aspect-ratio: 1 / 1;
          transform: translate(-50%, -50%) rotate(var(--rot));
          opacity: var(--sa-fst26-baby-opacity, 1);
          background-color: var(--sa-fst26-icon-color, #444249);
          -webkit-mask-image: var(--sa-fst26-icon-mask);
          mask-image: var(--sa-fst26-icon-mask);
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
          -webkit-mask-position: center;
          mask-position: center;
          -webkit-mask-size: contain;
          mask-size: contain;
          transition: background-color 250ms ease, opacity 250ms ease;
          will-change: background-color, opacity;
        }

        @media only screen and (max-width: 767px) {
          .sa-fst26 .sa-fst26__baby-pattern-icon {
            transform: translate(-50%, -50%) rotate(var(--rot)) scale(0.72);
          }

          .sa-fst26 .sa-fst26__baby-pattern-icon:nth-child(even) {
            display: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sa-fst26 .sa-fst26__baby-pattern-icon {
            transition: none;
          }
        }
      `;

      const iconUrls = [
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_9_89c2c99c-362d-4ae8-b1b4-1c43b1524ef0.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_7_2187cd87-16fb-4200-a710-ad77288ba3ac.svg?v=1787906492',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_8.svg?v=1787906474',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_6_21473ce0-49b4-4d06-8cfb-c0961db800cd.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_1_4aa3b8e9-2d69-4807-9799-2b8a71125ea3.svg?v=1787906484',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_3_7483a27a-dd27-4382-b22a-0cbea67676fa.svg?v=1787906492',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_7.svg?v=1787906474',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_4_cc33e49a-3bf0-49b3-a087-068c41ee8d6c.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_6_e7d8ac8b-7817-4448-8cc1-03e3a4de7d64.svg?v=1787906492',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_7_acc6f164-dc28-4158-8f75-358324a517ea.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_10.svg?v=1787906475',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_1_bbefcdab-eef8-448b-bd93-ab35acb1b75e.svg?v=1787906491',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_9.svg?v=1787906474',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_2_53815375-5383-4b0d-b9dd-50dc370bc223.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_5_0a3ec276-9c6f-45e9-bb21-f920fce52d60.svg?v=1787906492',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_3.svg?v=1787906475',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_8_bf9aa2ef-5526-40c3-a342-fba6e5688b19.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_1_6ba5e2aa-bde1-4053-9718-c481fb8c9d37.svg?v=1787906474',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_4_ed2aea54-5091-4dbc-bd61-9c122cc81228.svg?v=1787906492',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_5_59b4052a-6790-4aee-8534-34a379325ab5.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_5.svg?v=1787906475',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_2_29c57678-b3ae-475b-9c40-bd1d78a1fea0.svg?v=1787906492',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_11.svg?v=1787906474',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_3_f5a1cfca-7401-4ba4-a608-ee66b55864e0.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_2.svg?v=1787906475',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_10_c587bc16-7ea7-4339-a083-7a9984ac720e.svg?v=1787906485',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_4.svg?v=1787906474',
        'https://cdn.shopify.com/s/files/1/0536/5642/0538/files/Asset_6.svg?v=1787906474'
      ];

      const rows = [
        { y: 5, xs: [4, 20, 36, 52, 68, 84] },
        { y: 18, xs: [12, 28, 44, 60, 76, 92] },
        { y: 31, xs: [4, 20, 36, 52, 68, 84] },
        { y: 44, xs: [12, 28, 44, 60, 76, 92] },
        { y: 57, xs: [4, 20, 36, 52, 68, 84] },
        { y: 70, xs: [12, 28, 44, 60, 76, 92] },
        { y: 83, xs: [4, 20, 36, 52, 68, 84] },
        { y: 96, xs: [12, 28, 44, 60, 76, 92] }
      ];

      const xJitter = [0, 1.4, -1.2, 0.8, -1.5, 1.1, -0.6, 1.7, -1, 0.4, -1.3, 1.2];
      const yJitter = [0, -0.8, 1.1, -0.5, 0.7, -1.1, 0.4, 1, -0.7, 0.6, -0.9, 0.3];
      const sizes = [42, 54, 36, 48, 44, 58, 40, 50, 38, 56, 46, 52, 34, 48, 44, 60];
      const rotations = [-12, 8, -5, 13, -9, 4, 11, -7, 6, -14, 9, -3, 12, -10, 5, -8];

      const pattern = document.createElement('div');
      pattern.className = 'sa-fst26__baby-pattern';
      pattern.setAttribute('aria-hidden', 'true');

      let index = 0;
      rows.forEach((row, rowIndex) => {
        row.xs.forEach((baseX, colIndex) => {
          const icon = document.createElement('span');
          icon.className = 'sa-fst26__baby-icon sa-fst26__baby-pattern-icon';

          const x = baseX + xJitter[(index + rowIndex) % xJitter.length];
          const y = row.y + yJitter[(index + colIndex) % yJitter.length];
          const size = sizes[(index * 3 + rowIndex) % sizes.length];
          const rotation = rotations[(index * 5 + colIndex) % rotations.length];
          const url = iconUrls[index % iconUrls.length];

          icon.style.setProperty('--x', `${x}%`);
          icon.style.setProperty('--y', `${y}%`);
          icon.style.setProperty('--size', `${size}px`);
          icon.style.setProperty('--rot', `${rotation}deg`);
          icon.style.setProperty('--sa-fst26-icon-mask', `url("${url}")`);

          pattern.appendChild(icon);
          index += 1;
        });
      });

      this.shell.prepend(pattern);
    }

    onTabClick(event) {
      event.preventDefault();
      this.activate(event.currentTarget.dataset.saFst26Tab, true);
    }

    onTabKeydown(event) {
      const currentIndex = this.buttons.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.buttons.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.buttons.length - 1;
      if (nextIndex === currentIndex) return;

      event.preventDefault();
      const nextButton = this.buttons[nextIndex];
      this.activate(nextButton.dataset.saFst26Tab, true);
      nextButton.focus();
    }

    activate(targetId, smoothScroll) {
      const currentPanel = this.panels.find((panel) => panel.classList.contains('is-active'));
      if (currentPanel && currentPanel.dataset.saFst26Panel === targetId) return;
      const targetPanel = this.panels.find((panel) => panel.dataset.saFst26Panel === targetId);
      if (!targetPanel) return;

      let activeButton = null;

      this.buttons.forEach((button) => {
        const isActive = button.dataset.saFst26Tab === targetId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive) activeButton = button;
      });

      if (smoothScroll && activeButton && this.tabList) {
        const targetLeft = activeButton.offsetLeft - (this.tabList.clientWidth / 2) + (activeButton.clientWidth / 2);
        this.tabList.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'auto'
        });
      }

      this.panels.forEach((panel) => {
        const isActive = panel === targetPanel;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.syncBackground(targetPanel);
      this.resyncPanel(targetPanel, { resetPosition: true });
      document.dispatchEvent(new CustomEvent('sa-fst26:tab-changed', {
        detail: { sectionId: this.closest('.sa-fst26')?.id, panelId: targetId }
      }));
    }

    installStabilityWatchers() {
      window.addEventListener('load', this.onWindowLoad);
      window.addEventListener('pageshow', this.onWindowLoad);
    }

    scheduleResync(delay = 0) {
      if (this.resyncTimer) clearTimeout(this.resyncTimer);
      this.resyncTimer = setTimeout(() => this.resyncActivePanel(), delay);
    }

    getActivePanel() {
      return this.panels.find((panel) => panel.classList.contains('is-active'));
    }

    resyncActivePanel() {
      const active = this.getActivePanel();
      if (!active) return;
      this.resyncPanel(active);
    }

    resyncPanel(panel, options = {}) {
      this.refreshCarousel(panel, options);
      this.refreshProductCards(panel);
    }

    refreshCarousel(panel, options = {}) {
      if (this.displayType !== 'carousel') return;
      const slider = panel.querySelector('slide-show');
      if (!slider || !window.Flickity) return;
      const resetPosition = options.resetPosition === true;

      const attemptRefresh = (attempt = 0) => {
        const flkty = window.Flickity.data(slider);
        if (!flkty) {
          if (attempt < 24) setTimeout(() => attemptRefresh(attempt + 1), attempt < 6 ? 90 : 150);
          return;
        }
        flkty.reloadCells();
        flkty.resize();
        flkty.reposition();
        if (resetPosition && flkty.selectedIndex !== 0) flkty.select(0, false, true);
        if (attempt < 2) {
          setTimeout(() => attemptRefresh(attempt + 1), 120);
          return;
        }
      };

      requestAnimationFrame(() => setTimeout(() => attemptRefresh(0), 60));
    }

    refreshProductCards(panel) {
      if (!panel) return;

      const trigger = () => {
        if (window.lazySizes?.loader?.checkElems) window.lazySizes.loader.checkElems();
        if (window.lazySizes?.autoSizer?.checkElems) window.lazySizes.autoSizer.checkElems();
        document.dispatchEvent(new CustomEvent('sa-fst26:products-refreshed', {
          detail: { sectionId: this.closest('.sa-fst26')?.id, panelId: panel.dataset.saFst26Panel }
        }));
      };

      requestAnimationFrame(trigger);
      setTimeout(trigger, 180);
    }

    syncBackground(panel) {
      if (!this.shell) return;
      const targetBg = panel.dataset.saFst26Bg;
      if (targetBg) this.shell.style.setProperty('--sa-fst26-tab-bg', targetBg);
      const iconColor = panel.dataset.saFst26IconColor;
      if (iconColor) this.shell.style.setProperty('--sa-fst26-icon-color', iconColor);
    }
  }

  customElements.define('sa-fst26-tabs', SaFst26Tabs);
}
